import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { configService } from '../../services/configService';
import { Country } from '../../types';
import { debug } from '../../utils/debug';
import FileLoad from '../FileLoad';
import CountrySelector from '../CountrySelector';
import { DeviceCapabilities } from '../../types/client';
import { ClientImageProcessor } from '../../services/client/imageProcessor';
import { useWizard } from '../wizard/WizardContext';
import Icon from '../common/Icon';
import { ICONS } from '../../utils/fontAwesome';
import { trackPhotoUpload, trackCountrySelect, trackUploadError } from '../../utils/analytics';

const LoadStep: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [countries, setCountries] = useState<Country[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deviceCapabilities, setDeviceCapabilities] = useState<DeviceCapabilities | null>(null);
  const [isClientSupported, setIsClientSupported] = useState(true);

  const { state, setFile, setCountry, completeStep, resetProcessingState } = useWizard();

  useEffect(() => {
    // Check device capabilities on load
    const processor = new ClientImageProcessor({ demoMode: true });
    const capabilities = processor.getCapabilities();
    const supported = processor.isSupported();

    setDeviceCapabilities(capabilities);
    setIsClientSupported(supported);

    debug.client('Privacy-first mode enabled by default');
    processor.dispose();
  }, []);

  const loadCountries = useCallback(() => {
    try {
      const countriesData = configService.getCountries();
      setCountries(countriesData);

      // Check URL for country parameter
      const countryParam = searchParams.get('country');
      if (countryParam) {
        const foundCountry = countriesData.find(c => c.code === countryParam);
        if (foundCountry && (!state.selectedCountry || state.selectedCountry.code !== countryParam)) {
          setCountry(foundCountry);
        }
      } else {
        // Set default country if no URL param and none selected (but don't update URL)
        if (!state.selectedCountry && countriesData.length > 0) {
          const defaultCountry = configService.getDefaultCountry();
          if (defaultCountry) {
            setCountry(defaultCountry);
            // Don't automatically set URL parameter for default country
          }
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error loading countries';
      debug.error('Error loading countries from config:', error);
      setError(t('errors.loadCountriesFailed'));

      // Track error
      trackUploadError(errorMessage);
    }
  }, [t, state.selectedCountry, setCountry, searchParams]);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  const handleFileSelect = (file: File) => {
    resetProcessingState();
    setFile(file);
    setError(null);

    // Track photo upload event
    try {
      trackPhotoUpload(file.size, state.selectedCountry?.code);
    } catch (error) {
      debug.error('Failed to track photo upload:', error);
    }
  };

  const handleCountrySelect = (country: Country) => {
    if (state.selectedCountry && state.selectedCountry.code !== country.code) {
      resetProcessingState();
    }
    setCountry(country);
    setSearchParams({ country: country.code });
    setError(null);

    // Track country selection event
    try {
      trackCountrySelect(country.code);
    } catch (error) {
      debug.error('Failed to track country select:', error);
    }
  };

  const handleStartProcessing = () => {
    if (!state.uploadedFile || !state.selectedCountry) {
      setError(t('errors.selectBoth'));
      return;
    }

    completeStep('load');
    navigate('/crop.html');
  };

  const canStartProcessing = state.uploadedFile && state.selectedCountry;

  return (
    <div className="space-y-6">
      {/* Device Compatibility Notice */}
      {!isClientSupported && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon icon={ICONS.INFO_CIRCLE} className="text-blue-600 text-xl mt-1" />
            <div>
              <h3 className="font-semibold text-blue-800">Demo Mode Active</h3>
              <p className="text-blue-700 text-sm mt-1">
                Running in demonstration mode for maximum device compatibility.
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Action Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 space-y-8 border-2 border-blue-100 dark:border-blue-800">
        <div className="space-y-6">
          {/* Step 1: Country Selection */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2 mb-3">
              <span className="bg-blue-600 text-white text-sm font-medium px-2 py-1 rounded">1</span>
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('workflow.selectCountry')}</h3>
            </div>
            <CountrySelector
              countries={countries}
              selectedCountry={state.selectedCountry}
              onCountrySelect={handleCountrySelect}
              disabled={false}
            />
          </div>

          {/* Step 2: Photo Load */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2 mb-3">
              <span className={`text-sm font-medium px-2 py-1 rounded ${state.selectedCountry ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-500'}`}>2</span>
              <h3 className={`font-semibold ${state.selectedCountry ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>{t('workflow.loadPhoto')}</h3>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column: File Load */}
              <div>
                <FileLoad
                  onFileSelect={handleFileSelect}
                  selectedFile={state.uploadedFile}
                  disabled={!state.selectedCountry}
                />
                {!state.selectedCountry && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{t('workflow.selectCountryFirst')}</p>
                )}
              </div>

              {/* Right Column: Privacy Info */}
              <div className="bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon icon={ICONS.LOCK} className="text-green-600 text-xl" />
                  <h4 className="font-semibold text-green-800 dark:text-green-200">{t('privacy.processing')}</h4>
                </div>
                <div className="text-sm text-green-700 dark:text-green-300 space-y-2">
                  <p className="font-medium mb-2">{t('privacy.howItWorks')}</p>
                  <div className="space-y-1">
                    <p>• {t('privacy.step1')}</p>
                    <p>• {t('privacy.step2')}</p>
                    <p>• {t('privacy.step3')}</p>
                    <p>• {t('privacy.step4')}</p>
                  </div>
                  <p className="text-xs mt-2 font-medium">
                    {t('privacy.localProcessing')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={handleStartProcessing}
          disabled={!canStartProcessing}
          className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
        >
          <Icon icon={ICONS.LOCK} className="mr-2" />
          {t('workflow.startProcessing')}
        </button>
      </div>

      {/* Technical Information - Only shown in debug mode */}
      {debug.isEnabled() && deviceCapabilities && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <details className="cursor-pointer">
            <summary className="font-medium text-gray-900 dark:text-white mb-4">Device Information & Capabilities</summary>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Device Type:</span>
                <span className="ml-2">{deviceCapabilities.isMobile ? 'Mobile' : 'Desktop'}</span>
              </div>
              <div>
                <span className="font-medium">WebGL Support:</span>
                <span className={`ml-2 ${deviceCapabilities.webglSupported ? 'text-green-600' : 'text-red-600'}`}>
                  {deviceCapabilities.webglSupported ? <><Icon icon={ICONS.CHECK} className="text-green-600" /> Yes</> : '✗ No'}
                </span>
              </div>
              <div>
                <span className="font-medium">Available Memory:</span>
                <span className="ml-2">{deviceCapabilities.availableMemory}GB</span>
              </div>
              <div>
                <span className="font-medium">CPU Cores:</span>
                <span className="ml-2">{deviceCapabilities.hardwareConcurrency}</span>
              </div>
              <div>
                <span className="font-medium">WebGPU Support:</span>
                <span className={`ml-2 ${deviceCapabilities.webgpuSupported ? 'text-green-600' : 'text-gray-500'}`}>
                  {deviceCapabilities.webgpuSupported ? <><Icon icon={ICONS.CHECK} className="text-green-600" /> Yes</> : 'No'}
                </span>
              </div>
              <div>
                <span className="font-medium">Connection:</span>
                <span className="ml-2">{deviceCapabilities.connection || 'Unknown'}</span>
              </div>
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export default LoadStep;